import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { lazy, useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import { Provider } from 'react-redux';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import { PersistGate } from 'redux-persist/integration/react';

import { PoliciesTabsEnum } from './common/enum';
import useBreakpoint from './common/hooks/useBreakpoint';
import { loadAllFont } from './common/utils/loadFont';
import { authGuard, routes, unAuthGuard } from './common/utils/routes';
import LoadingView from './components/Loading/LoadingModal';
import PrivateRoute from './components/Route/PrivateRoute';
import UserCognitoService from './services/controllers/user/UserCognitoService';
import store, { persistor } from './store';

import { TouchBackend } from 'react-dnd-touch-backend';
import { Test } from './Example';
import './index.scss';

const DashboardView = lazy(() => import('./views/dashboard/DashboardView'));
const LibraryView = lazy(() => import('./views/library/LibraryView'));
const UserManagementView = lazy(() => import('./views/user-management/UserManagementView'));
const AccountManagementView = lazy(() => import('./views/account-management/AccountManagementView'));
const ReferralsView = lazy(() => import('./views/referrals/ReferralsView'));
const SettingView = lazy(() => import('./views/setting/SettingView'));
const HelpAndSupportView = lazy(() => import('./views/help-and-support/HelpAndSupportView'));
const NavBar = lazy(() => import('./components/NavBar/NavBar'));
const SideBar = lazy(() => import('./components/SideBar/SideBar'));
const BookNavigatorMenu = lazy(() => import('./views/book-navigator-menu/BookNavigatorMenu'));
const LoginView = lazy(() => import('./views/login/LoginView'));
const ProfileView = lazy(() => import('./views/profile/ProfileView'));
const BookCreateView = lazy(() => import('./views/book-create/BookCreateView'));
const ResetPasswordView = lazy(() => import('./views/reset-password/ResetPassword'));
const GuestLandingPage = lazy(() => import('./views/GuestLandingPage/GuestLandingPage'));
const ArchiveView = lazy(() => import('./views/library-archive/ArchiveView'));
const PoliciesView = lazy(() => import('./views/policies/PoliciesView'));
const VerifyAccountView = lazy(() => import('./views/verify-account/VerifyAccountView'));
const MobileUploadView = lazy(() => import('./views/library/mobile-upload/MobileUploadView'));
const GuestMobileUpload = lazy(() => import('./views/guest-mobile-upload/GuestMobileUpload'));

export function getMobileOperatingSystem() {
  var userAgent = navigator.userAgent || navigator.vendor || (window as any)?.opera;

  // Windows Phone must come first because its UA also contains "Android"
  if (/windows phone/i.test(userAgent)) {
    return 'Windows Phone';
  }

  if (/android/i.test(userAgent)) {
    return 'Android';
  }

  // iOS detection from: http://stackoverflow.com/a/9039885/177710
  if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any)?.MSStream) {
    return 'iOS';
  }

  return 'unknown';
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

export const useCheckMobileView = () => {
  const [isMobileView, setIsMobileView] = useState(false);
  const breakpoint = useBreakpoint();

  useEffect(() => {
    const mobileView = () => {
      switch (breakpoint) {
        case 'xs':
          return true;
        default:
          return false;
      }
    };
    setIsMobileView(mobileView());
  }, [breakpoint]);
  return { isMobileView };
};

const HomeWrapper: React.FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <NavBar />
      <SideBar />
      <BookNavigatorMenu />
      <div className="wrapper">
        <Routes>
          <Route path={routes.DEFAULT} element={<PrivateRoute guards={[authGuard]} element={<DashboardView />} />} />
          <Route path={routes.TEMPLATES} element={<PrivateRoute guards={[authGuard]} element={<DashboardView />} />} />
          <Route path={routes.LIBRARY} element={<PrivateRoute guards={[authGuard]} element={<LibraryView />} />} />
          <Route path={routes.LIBRARY_ARCHIVE} element={<PrivateRoute guards={[authGuard]} element={<ArchiveView />} />} />
          <Route path={routes.USER_MANAGEMENT} element={<PrivateRoute guards={[authGuard]} element={<UserManagementView />} />} />
          <Route path={routes.ACCOUNT_MANAGEMENT} element={<PrivateRoute guards={[authGuard]} element={<AccountManagementView />} />} />
          <Route path={routes.REFERRALS} element={<PrivateRoute guards={[authGuard]} element={<ReferralsView />} />} />
          <Route path={routes.SETTING} element={<PrivateRoute guards={[authGuard]} element={<SettingView />} />} />
          <Route path={routes.HELP_AND_SUPPORT} element={<PrivateRoute guards={[authGuard]} element={<HelpAndSupportView />} />} />
          <Route path={routes.PROFILE} element={<PrivateRoute guards={[authGuard]} element={<ProfileView />} />} />
          <Route path={routes.CREATE_BOOK} element={<PrivateRoute guards={[authGuard]} element={<BookCreateView />} />} />
          <Route
            path={routes.POLICIES_PRIVACY}
            element={<PrivateRoute guards={[authGuard]} element={<PoliciesView tab={PoliciesTabsEnum.PRIVACY} />} />}
          />
        </Routes>
      </div>
    </>
  );
};

const insertTheme = () => {
  const root = document.querySelector(':root');
  const favicons = document.querySelectorAll("link[rel~='icon']") as NodeListOf<HTMLLinkElement>;
  if (root && favicons) {
    const appName = getComputedStyle(root).getPropertyValue('--authory-applicationName');
    document.title = appName;
    const faviconSrc = [
      getComputedStyle(root).getPropertyValue('--authory-favicon-16'),
      getComputedStyle(root).getPropertyValue('--authory-favicon-32'),
      getComputedStyle(root).getPropertyValue('--authory-favicon-64'),
    ];

    favicons.forEach((favicon: HTMLLinkElement, index: number) => {
      favicon.href = faviconSrc[index];
    });
  }
};

const App: React.FC = () => {
  const { i18n } = useTranslation();
  const detectLanguage = async () => {
    const user = UserCognitoService.cognitoUser;
    if (user) {
      const userInfo: any = await UserCognitoService.getUserAttributes();
      const currenLang = localStorage.getItem('i18nextLng');
      const userLang = userInfo?.language;
      if (currenLang !== userLang) {
        i18n.changeLanguage(userLang);
      }
    }
  };

  useEffect(() => {
    loadAllFont();
    insertTheme();
    detectLanguage();
  }, []);

  const device = getMobileOperatingSystem();

  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <DndProvider
            backend={device === 'unknown' ? HTML5Backend : TouchBackend}
            options={{
              enableTouchEvents: device !== 'unknown',
            }}
          >
            <React.Suspense fallback={<LoadingView open />}>
              <BrowserRouter>
                <ToastContainer />
                <LoadingView />
                <div className="container-app">
                  <Routes>
                    <Route path={routes.LOGIN} element={<PrivateRoute guards={[unAuthGuard]} element={<LoginView />} />} />
                    <Route path={routes.REGISTER} element={<PrivateRoute guards={[unAuthGuard]} element={<LoginView />} />} />
                    <Route path={routes.FORGOT} element={<PrivateRoute guards={[unAuthGuard]} element={<LoginView />} />} />
                    <Route path={routes.RESET_PASSWORD} element={<PrivateRoute guards={[unAuthGuard]} element={<ResetPasswordView />} />} />
                    <Route path={routes.VERIFY_ACCOUNT} element={<PrivateRoute guards={[unAuthGuard]} element={<VerifyAccountView />} />} />
                    <Route path={routes.POLICIES_TERMS} element={<PoliciesView tab={PoliciesTabsEnum.TERMS} />} />
                    <Route path={routes.LANDING_PAGE} element={<PrivateRoute guards={[unAuthGuard]} element={<GuestLandingPage />} />} />
                    <Route path={routes.MOBILE_UPLOAD} element={<MobileUploadView />} />
                    <Route path={routes.GUEST_MOBILE_UPLOAD} element={<GuestMobileUpload />} />
                    <Route path="/test" element={<Test />} />
                    <Route path="*" element={<PrivateRoute guards={[authGuard]} element={<HomeWrapper />} />} />
                  </Routes>
                  <Tooltip id="authory-tooltip" className="authory-tooltip-styles" />
                </div>
              </BrowserRouter>
            </React.Suspense>
          </DndProvider>
        </PersistGate>
      </Provider>
    </QueryClientProvider>
  );
};

export default App;
